/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useGatewaySipProfileList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing Gateway SIP Profile list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchGatewaySipProfiles } from '../api';
import { GatewaySipProfile } from '../types';

interface UseGatewaySipProfileListProps {
  initialColumns?: DataGridColumn<GatewaySipProfile>[];
  domainId?: number | null;
  onRefreshNeeded?: () => void;
}

/**
 * Custom React hook for managing the Gateway SIP Profiles DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * Supports Domain Contextual Filtering to only show profiles belonging to the current tenant.
 * 
 * @param props - Configuration properties for the list hook.
 * @returns Combined DataGrid state and the effective Domain ID applied to the filter.
 */
export function useGatewaySipProfileList(props: UseGatewaySipProfileListProps = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchProfiles = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const result = await fetchGatewaySipProfiles({
        ...params,
        domainId: effectiveDomainId,
      });
      return {
        data: result.data || [],
        pagination: result.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    } catch (error) {
      return {
        data: [] as GatewaySipProfile[],
        pagination: {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }, [effectiveDomainId]);

  const dataGridResult = useDataGrid({
    fetchData: fetchProfiles,
    initialColumns: props.initialColumns || [],
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'gatewaySipProfiles',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...dataGridResult,
    effectiveDomainId,
  };
}

